import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Button, Tooltip, message } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts';
import { useIsAdmin } from 'hooks/useIsAdmin';
import {
  useEndProfileReservationOrVisitsInRangeMutation,
  useGetAutoDeskBookingByUserIdForProfileDesksPanelQuery,
  useGetAutoDeskBookingForProfileDesksPanelQuery,
} from 'generated/graphql';
import { SHELL_OVERLAY_POPUP_Z_INDEX } from 'constants/zIndex';
import { useProfilePageUserContext } from '../../ProfilePageUserContext';
import { ProfileReservation } from '../../BookingsList/utils/getReservationActions';
import { CancelAutoBookingsModal } from './CancelAutoBookingsModal';

type CancelAutoBookingsProps = {
  userId: string;
  /** The auto-booked (creationType AUTOMATIC) flex reservations in view. */
  autoBookedReservations: ProfileReservation[];
  isProfileSidebarOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
};

const ADVANCED_BOOKING_THRESHOLD_SLUG =
  'seat-reservation-advanced-booking-threshold';

// Auto desk booking never books further out than this, so cancelling the next
// 31 days clears every upcoming auto-booking (mirrors the legacy page).
const CANCEL_RANGE_DAYS = 31;

/**
 * The "Cancel auto-bookings" action on the fullscreen profile's Desks tab,
 * ported from the legacy profile page (appv2 ADBCancelContainer). Rendered for
 * the profile's owner and for admins, and only when the auto-desk-booking
 * setting is on for the profile's user; disabled (with an explanatory tooltip)
 * while the user has no upcoming auto-booked reservations.
 */
export const CancelAutoBookings = ({
  userId,
  autoBookedReservations,
  isProfileSidebarOpen = true,
  onModalOpenChange,
}: CancelAutoBookingsProps): JSX.Element | null => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'desks.autoBookings',
  });
  const { currentOrg, currentUser } = useAuthContext();
  const { user: profileUser } = useProfilePageUserContext();
  const [messageApi, messageContextHolder] = message.useMessage();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isCurrentPerson = currentUser?.id === userId;
  // Cancelling someone else's auto-bookings is an admin action, matching the
  // legacy person view (which gated on OrganizationService.isAdmin) — not the
  // broader users:manage permission.
  const { isAdmin } = useIsAdmin();

  // The legacy page reads the viewer's own ADB setting on their profile and
  // the by-user-id variant (admin-gated in the gateway) on everyone else's.
  const { data: ownAdbData } = useGetAutoDeskBookingForProfileDesksPanelQuery({
    skip: !isCurrentPerson,
    errorPolicy: 'all',
  });
  const { data: userAdbData } =
    useGetAutoDeskBookingByUserIdForProfileDesksPanelQuery({
      variables: { userId },
      skip: isCurrentPerson || !isAdmin,
      errorPolicy: 'all',
    });

  const adbEnabled = isCurrentPerson
    ? ownAdbData?.getAutoDeskBooking.autoDeskBooking ?? false
    : userAdbData?.getAutoDeskBookingByUserId.autoDeskBooking ?? false;

  const [endReservationOrVisitsInRange, { loading: cancelling }] =
    useEndProfileReservationOrVisitsInRangeMutation({
      refetchQueries: ['GetProfileBookings', 'GetProfileSeriesInstances'],
      awaitRefetchQueries: true,
    });

  const timezone =
    autoBookedReservations[0]?.timeZone ??
    autoBookedReservations[0]?.seat.location.timezone ??
    'UTC';

  // Auto-booking resumes once "now + advanced booking threshold" passes the
  // cancelled range, so surface the location's threshold when it's inside the
  // 31-day horizon (same derivation as the legacy container).
  const resumeDate = useMemo(() => {
    const thresholdValue = autoBookedReservations[0]?.seat.settings?.find(
      (setting) => setting?.slug === ADVANCED_BOOKING_THRESHOLD_SLUG
    )?.value;
    const threshold = thresholdValue ? moment.duration(thresholdValue) : null;

    return threshold && threshold.asDays() <= CANCEL_RANGE_DAYS
      ? moment().add(threshold).format('L')
      : moment().tz(timezone).add(CANCEL_RANGE_DAYS, 'days').format('L');
  }, [autoBookedReservations, timezone]);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
    onModalOpenChange?.(true);
  }, [onModalOpenChange]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    onModalOpenChange?.(false);
  }, [onModalOpenChange]);

  // Close alongside the drawer or when the profile switches to another user.
  useEffect(() => {
    if (!isProfileSidebarOpen) {
      closeModal();
    }
  }, [closeModal, isProfileSidebarOpen]);
  useEffect(() => {
    closeModal();
  }, [closeModal, userId]);

  const handleConfirm = useCallback(
    async (shouldCancelVisits: boolean) => {
      if (!currentOrg) {
        return;
      }

      // From tomorrow (today's booking is left alone, like the legacy page)
      // through the auto-booking horizon.
      const start = moment().tz(timezone).startOf('day').add(1, 'day');
      const end = moment().tz(timezone).add(CANCEL_RANGE_DAYS, 'days');

      try {
        await endReservationOrVisitsInRange({
          variables: {
            orgId: currentOrg.id,
            userId,
            start: start.toISOString(),
            end: end.toISOString(),
            cancelFutureVisits: shouldCancelVisits,
            creationType: ['AUTOMATIC'],
          },
        });
        void messageApi.success(t('success'));
        closeModal();
      } catch {
        void messageApi.error(t('error'));
      }
    },
    [
      closeModal,
      currentOrg,
      endReservationOrVisitsInRange,
      messageApi,
      t,
      timezone,
      userId,
    ]
  );

  // Owner or admin only; anyone else renders nothing, not a disabled button.
  if (!adbEnabled || (!isCurrentPerson && !isAdmin)) {
    return null;
  }

  const disabled = autoBookedReservations.length === 0;
  const tooltipTitle = disabled
    ? isCurrentPerson
      ? t('noBookingsTooltipSelf')
      : t('noBookingsTooltipOther', {
          name: profileUser?.given_name ?? profileUser?.name ?? '',
        })
    : '';

  return (
    <>
      {messageContextHolder}
      <Tooltip title={tooltipTitle} zIndex={SHELL_OVERLAY_POPUP_Z_INDEX}>
        {/* span so the tooltip still gets hover events when the button is
            disabled (disabled buttons swallow them) */}
        <TooltipTarget>
          <Button danger disabled={disabled} onClick={openModal}>
            {t('button')}
          </Button>
        </TooltipTarget>
      </Tooltip>
      <CancelAutoBookingsModal
        open={isProfileSidebarOpen && isModalOpen}
        reservationCount={autoBookedReservations.length}
        resumeDate={resumeDate}
        loading={cancelling}
        onClose={closeModal}
        onConfirm={handleConfirm}
      />
    </>
  );
};

const TooltipTarget = styled.span`
  display: inline-block;
`;
